import React from "react";
import styled from "@emotion/styled/macro";
import { Typography, Modal, tokens } from "@sunrun/experience-ui-components";
import { ErrorComponent } from "components/atoms/ErrorComponent";

type ProductErrorModalProps = {
  action?: string;
  title?: string;
  context: React.ComponentProps<typeof ErrorComponent>["context"];
  error: React.ComponentProps<typeof ErrorComponent>["error"];
  onClose?: () => void;
  onRetry?: () => void;
  errorFromAnotherSystem?: boolean;
  severity?: React.ComponentProps<typeof ErrorComponent>["severity"];
};

const reportBugUrl =
  "https://sunrun.service-now.com/help?id=sc_cat_item&sys_id=d0eab1f94fd84600170809d14210c7f3&sysparm_category=a3499ce4db570c10fb951e1339961972&spa=1";

const ErrorModal = ({
  action,
  context,
  error,
  onClose,
  onRetry,
  title,
  errorFromAnotherSystem = false,
  severity,
}: ProductErrorModalProps) => {
  const formattedAction = action
    ? action[0].toUpperCase() + action.slice(1)
    : "An action";
  let buttons = {};
  if (onRetry && onClose) {
    buttons = {
      primaryButton: { text: "Retry", onClick: onRetry },
      secondaryButton: { text: "Dismiss", onClick: onClose },
    };
  } else if (onRetry) {
    buttons = {
      primaryButton: { text: "Retry", onClick: onRetry },
    };
  } else if (onClose) {
    buttons = {
      secondaryButton: { text: "Dismiss", onClick: onClose },
      primaryButton: {
        text: "Report bug",
        onClick: () => window.open(reportBugUrl, "_blank"),
      },
    };
  }
  return (
    <Modal
      testId="error-modal"
      type="error"
      title={title || "Something went wrong"}
      {...buttons}
      onClose={onClose}
      hideClose
    >
      <Grid>
        {errorFromAnotherSystem ? (
          <Typography
            color={tokens.TINTS_OFF_WHITE_20}
            style={{ fontSize: 20, marginBottom: 20 }}
          >
            ONE received an error from another system. File a bug at{" "}
            <a href={reportBugUrl} target="_blank" rel="noreferrer">
              help.sunrun.com
            </a>{" "}
            and contact SSD with the customer address and the error message
            shown below. This is not a bug from ONE but from another technical
            system that ONE relies on.
          </Typography>
        ) : (
          <Typography style={{ marginBottom: 12 }}>
            {"Our system is having trouble "}
            {action ?? "processing your preferences"} at the moment. Please try
            again to resolve the issue.
          </Typography>
        )}
        {action && (
          <Typography
            component="p"
            variant="p"
          >{`${formattedAction} failed to complete.`}</Typography>
        )}

        <ErrorComponent
          context={context}
          action={formattedAction}
          error={error}
          errorFromAnotherSystem={errorFromAnotherSystem}
          severity={severity}
        />
      </Grid>
    </Modal>
  );
};

const Grid = styled.div`
  display: grid;
`;

export { ErrorModal };
export type { ProductErrorModalProps };
